<template>
  <div class="atb-flex atb-flex-row atb-gap-4 atb-py-6 atb-px-4">
    <div class="atb-flex-shrink">
      <Sidebar
        is-header
        color="basic"
        class="atb-border atb-rounded-lg"
        sidebar-opened-class="atb-w-76"
        section-class="atb-divide-y"
        sidebar-class="atb-min-h-[calc(100vh-61rem)]"
        header-class="atb-pt-0 atb-pr-0 atb-pl-6 atb-text-base atb-w-full atb-normal-case atb-font-medium atb-text-black"
        body-class="atb-flex atb-justify-center atb-items-center atb-p-2"
        :sidebar-style="{ minHeight: 'calc(100vh - (245px))' }"
        :toggle-open-style="{ transform: 'translateX(240px)' }"
      >
        <template #header>
          {{ $t('properties') }}
        </template>
        <template #body>
          <Accordion :style="{ width: '300px' }">
            <template #accordion>
              <AccordionDetails
                label="Raw Data"
                v-show="useDataSource"
              >
                <template #content>
                  <TreeItem :model="rawDataTreeItemNode" />
                </template>
              </AccordionDetails>
              <AccordionDetails
                label="Attributes"
                v-show="!useDataSource"
              >
                <template #content>
                  <Textbox
                    id="attributeSearch"
                    v-model="attributeSearch"
                    :readonly="readOnly"
                    container-label-class="atb-w-full"
                    :placeholder="$t('search')"
                  />
                  <draggable
                    class="atb-max-h-64 atb-overflow-y-auto atb-mt-1"
                    :list="filteredAttributeItems"
                    :sort="false"
                    item-key="id"
                  >
                    <template #item="{ element }">
                      <div
                        class="eds-menu-item menu-item-basic"
                        :key="element"
                        :title="element.shortName"
                      >
                        <span class="menu-item-label">
                          {{ element.shortName }}
                        </span>
                      </div>
                    </template>
                  </draggable>
                  <div
                    class="atb-flex atb-flex-row atb-justify-start atb-gap-2 atb-pt-4"
                  >
                    <Button
                      id="attributesRefresh"
                      type="submit"
                      motif="tertiary"
                      :title="$t('refresh')"
                      @click="refreshLists"
                    >
                      <template v-slot:icon>
                        <span class="material-icons">
                          {{ $t('assignmentReturned') }}
                        </span>
                      </template>
                      {{ $t('refresh') }}
                    </Button>
                    <Button
                      id="attributesReset"
                      type="submit"
                      motif="tertiary"
                      :title="$t('reset')"
                      @click="resetAttributesListSearch"
                    >
                      <template v-slot:icon>
                        <span class="material-icons"> {{ $t('sync') }} </span>
                      </template>
                      {{ $t('reset') }}
                    </Button>
                  </div>
                </template>
              </AccordionDetails>
              <AccordionDetails label="Filters">
                <template #content>
                  <Textbox
                    id="filterSearch"
                    :readonly="readOnly"
                    v-model="filterSearch"
                    container-label-class="atb-w-full"
                    :placeholder="$t('search')"
                  />
                  <draggable
                    class="atb-max-h-64 atb-overflow-y-auto atb-mt-1"
                    :list="filteredFilterItems"
                    :sort="false"
                    item-key="id"
                  >
                    <template #item="{ element }">
                      <div
                        class="eds-menu-item menu-item-basic"
                        :key="element"
                        :title="element.shortName"
                      >
                        <span class="menu-item-label">{{
                          element.shortName
                        }}</span>
                      </div>
                    </template>
                  </draggable>
                  <div
                    class="atb-flex atb-flex-row atb-justifydiv-start atb-gap-2 atb-pt-4"
                  >
                    <Button
                      id="filterRefresh"
                      type="submit"
                      motif="tertiary"
                      :title="$t('refresh')"
                      @click="refreshLists"
                    >
                      <template v-slot:icon>
                        <span class="material-icons">
                          {{ $t('assignmentReturned') }}
                        </span>
                      </template>
                      {{ $t('refresh') }}
                    </Button>
                    <Button
                      id="filterReset"
                      type="submit"
                      motif="tertiary"
                      :title="$t('reset')"
                      @click="resetFiltersListSearch"
                    >
                      <template v-slot:icon>
                        <span class="material-icons"> {{ $t('sync') }} </span>
                      </template>
                      {{ $t('reset') }}
                    </Button>
                  </div>
                </template>
              </AccordionDetails>
              <AccordionDetails label="Function">
                <template #content>
                  <Textbox
                    id="functionSearch"
                    :readonly="readOnly"
                    v-model="functionSearch"
                    container-label-class="atb-w-full"
                    :placeholder="$t('search')"
                  />
                  <draggable
                    class="atb-max-h-64 atb-overflow-y-auto atb-mt-1"
                    :list="filteredFunctionItems"
                    :sort="false"
                    item-key="id"
                  >
                    <template #item="{ element }">
                      <div
                        class="eds-menu-item menu-item-basic"
                        :key="element"
                        :title="element.code"
                      >
                        <span class="menu-item-label">
                          {{ element.functionName }}
                        </span>
                      </div>
                    </template>
                  </draggable>
                  <div
                    class="atb-flex atb-flex-row atb-justify-start atb-gap-2 atb-pt-4"
                  >
                    <Button
                      id="functionReset"
                      type="submit"
                      motif="tertiary"
                      :title="$t('reset')"
                      @click="resetFunctionsListSearch"
                    >
                      <template v-slot:icon>
                        <span class="material-icons"> {{ $t('sync') }} </span>
                      </template>
                      {{ $t('reset') }}
                    </Button>
                  </div>
                </template>
              </AccordionDetails>
            </template>
          </Accordion>
        </template>
      </Sidebar>
    </div>
    <CodeEditor
      :load-data="loadData"
      :readonly="readOnly"
      :lang="language"
      :theme="theme"
      :options="options"
      :wrap="wordWrap"
      :use-data-source="useDataSource"
      @save-clicked="save"
      @data-source-change="loadLuaCode"
      @data-source-segment-change="loadRawData"
      @set-code-editor-dirty-status="codeEditorDirtyStatusHandler"
      ref="codeEditorRef"
    />
  </div>
</template>

<script lang="ts">
  export default { name: 'CodeTab' };
</script>

<script lang="ts" setup>
  import Sidebar from '@atb-ui/eds-vue/src/components/Sidebar.vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Accordion from '@atb-ui/eds-vue/src/components/Accordion/Accordion.vue';
  import AccordionDetails from '@atb-ui/eds-vue/src/components/Accordion/AccordionDetails.vue';
  import Textbox from '@atb-ui/eds-vue/src/components/Textbox.vue';
  import draggable from 'vuedraggable';
  import CodeEditor from '@/app/shared/components/code-editor/CodeEditor.vue';
  import TreeItem from '@/app/shared/components/tree/TreeItem.vue';
  import { computed, ref, toRefs } from 'vue';
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { useLuaStore } from '@/app/shared/stores/lua-store';
  import { storeToRefs } from 'pinia';
  import { LoadLuaCodeEvent } from '@/app/features/ams/shared/types/events/code-tab-events.interface';
  import { SaveFilterCodeEvent } from '@/app/shared/types/events/filters-event.interface';
  import { CodeEditorLoadData } from '@/app/shared/types/code-editor-load-data.interface';
  import { SegmentInfo } from '@/app/shared/types/segment-info.interface';
  import { useI18n } from 'vue-i18n';

  interface CodeTabProps {
    clientId: number;
    codeMetaInformationsId: number;
    loadData: CodeEditorLoadData;
    readOnly: boolean;
    useDataSource?: boolean;
  }
  interface CodeTabEvent {
    (_event: 'loadLuaCode', _params: LoadLuaCodeEvent): void;
    (_event: 'saveCode', _params: SaveFilterCodeEvent): void;
    (
      _event: 'setDirtyStatus',
      stringField: string,
      booleanField: boolean
    ): void;
    (
      _event: 'autoLockDataSource',
      _params: { dataSourceName: string; filterId: number }
    ): void;
  }
  const emit = defineEmits<CodeTabEvent>();
  const props = withDefaults(defineProps<CodeTabProps>(), {
    useDataSource: false,
  });
  const { t } = useI18n();
  const clientStore = useClientStore();
  const luaStore = useLuaStore();
  const { attributeList, filterList, functionList } = storeToRefs(clientStore);
  const { rawDataTreeItemNode } = storeToRefs(luaStore);
  const { clientId, loadData, codeMetaInformationsId } = toRefs(props);
  const currentDataSourceName = ref('');
  const attributeSearch = ref('');
  const filterSearch = ref('');
  const functionSearch = ref('');
  const language = ref('lua');
  const theme = ref('chrome');
  const options = ref({
    fontFamily: 'monospace',
    highlightActiveLine: true,
    highlightGutterLine: false,
  });
  const wordWrap = ref(false);
  const codeEditorRef = ref();

  const filteredAttributeItems = computed(() =>
    attributeList.value.filter((item: any) =>
      item.shortName.toLowerCase().match(attributeSearch.value.toLowerCase())
    )
  );

  const filteredFilterItems = computed(() =>
    filterList.value.filter((item: any) =>
      item.shortName.toLowerCase().match(filterSearch.value.toLowerCase())
    )
  );

  const filteredFunctionItems = computed(() =>
    functionList.value.filter((item: any) =>
      item.functionName.toLowerCase().match(functionSearch.value.toLowerCase())
    )
  );

  const save = async (code: SaveFilterCodeEvent) => {
    emit('saveCode', code);
  };

  const refreshLists = () => {
    clientStore.fetchAttributeFilterFunctionList(clientId.value);
  };

  const resetAttributesListSearch = () => {
    attributeSearch.value = '';
  };

  const resetFiltersListSearch = () => {
    filterSearch.value = '';
  };

  const resetFunctionsListSearch = () => {
    functionSearch.value = '';
  };

  const loadLuaCode = (newDataSource: string) => {
    emit('autoLockDataSource', {
      dataSourceName: newDataSource,
      filterId: codeMetaInformationsId.value,
    });
    emit('loadLuaCode', { newDataSource });
    currentDataSourceName.value = newDataSource;
  };

  const loadRawData = (segmentInfo: SegmentInfo) => {
    luaStore.fetchRawData(
      clientId.value,
      currentDataSourceName.value,
      segmentInfo.segmentId
    );
  };

  const codeEditorDirtyStatusHandler = (isDirty: boolean) => {
    emit('setDirtyStatus', t('code'), isDirty);
  };

  // Caller will be FilterDetails, after rollback event
  const refreshCodeEditorOnRollback = (dataSourceName: string) => {
    codeEditorRef.value.refreshCodeEditorOnRollback(dataSourceName);
  };

  addEventListener('dragstart', (event: any) => {
    const dragText =
      event.target.__draggable_context?.element.functionTemplate ||
      event.target.innerText;
    event.dataTransfer.setData('text', dragText);
  });

  defineExpose({
    refreshCodeEditorOnRollback,
  });
</script>
